import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { TablePlanMarkerType, TablePlanResponse, TablePlanTileType } from './table-plan.model';

/**
 * TVG-98: Talks to the event-scoped Table Plan endpoints. Every response
 * carries the full plan, summary, guest roster, and preset catalogue so the
 * panel can re-render from a single payload after each mutation.
 */
@Injectable({ providedIn: 'root' })
export class TablePlanService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  load(eventId: number | string): Observable<TablePlanResponse> {
    return this.http.get<TablePlanResponse>(this.base(eventId));
  }

  addTable(
    eventId: number | string,
    payload: {
      label: string;
      seat_count?: number;
      tile_type?: TablePlanTileType;
      position_x?: number;
      position_y?: number;
      rotation?: 0 | 90 | 180 | 270;
    },
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/tables`, payload);
  }

  updateTable(
    eventId: number | string,
    tableId: number,
    payload: Partial<{
      label: string;
      seat_count: number;
      sort_order: number;
      tile_type: TablePlanTileType;
      trestle_count: number;
      position_x: number;
      position_y: number;
      rotation: 0 | 90 | 180 | 270;
      is_top_table: boolean;
    }>,
  ): Observable<TablePlanResponse> {
    return this.http.put<TablePlanResponse>(`${this.base(eventId)}/tables/${tableId}`, payload);
  }

  deleteTable(eventId: number | string, tableId: number): Observable<TablePlanResponse> {
    return this.http.delete<TablePlanResponse>(`${this.base(eventId)}/tables/${tableId}`);
  }

  updateSeat(
    eventId: number | string,
    seatId: number,
    payload: { event_guest_id?: number | null; is_high_chair?: boolean },
  ): Observable<TablePlanResponse> {
    return this.http.put<TablePlanResponse>(`${this.base(eventId)}/seats/${seatId}`, payload);
  }

  /** TVG-373: add a chair to a side of a table, at a place along it. */
  addChair(
    eventId: number | string,
    tableId: number,
    side: 'top' | 'right' | 'bottom' | 'left',
    positionIndex: number,
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(
      `${this.base(eventId)}/tables/${tableId}/chairs`,
      { side, position_index: positionIndex },
    );
  }

  /** TVG-373: move a chair to a side of a (possibly different) table. */
  moveChair(
    eventId: number | string,
    seatId: number,
    destTableId: number,
    side: 'top' | 'right' | 'bottom' | 'left',
    positionIndex: number,
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(
      `${this.base(eventId)}/seats/${seatId}/move`,
      { table_plan_table_id: destTableId, side, position_index: positionIndex },
    );
  }

  /**
   * TVG-373: join a table onto another — end to end into a run, or two single
   * trestles side by side into a square. The source's new spot comes along.
   */
  joinTable(
    eventId: number | string,
    targetTableId: number,
    sourceTableId: number,
    sourceX: number,
    sourceY: number,
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/tables/${targetTableId}/join`, {
      source_table_id: sourceTableId,
      source_position_x: Math.max(0, Math.round(sourceX)),
      source_position_y: Math.max(0, Math.round(sourceY)),
    });
  }

  /** TVG-132: remove a chair, returning it to the pool. */
  removeChair(eventId: number | string, seatId: number): Observable<TablePlanResponse> {
    return this.http.delete<TablePlanResponse>(`${this.base(eventId)}/seats/${seatId}`);
  }

  /** TVG-142: designate a table as the top table, or clear the top table (null). */
  setTopTable(eventId: number | string, tableId: number | null): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/top-table`, {
      table_plan_table_id: tableId,
    });
  }

  /** TVG-142: remove every table (and its seat assignments) from the plan. */
  clearAll(eventId: number | string): Observable<TablePlanResponse> {
    return this.http.delete<TablePlanResponse>(`${this.base(eventId)}/tables`);
  }

  applyPreset(eventId: number | string, presetId: number): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/apply-preset`, { preset_id: presetId });
  }

  /**
   * TVG-144: open a per-venue layout template as the live working canvas. The
   * generated tables (from the formation + seat total + room outline) are sent
   * so the server materialises exactly what the preview showed.
   */
  applyLayoutTemplate(
    eventId: number | string,
    templateKey: string,
    tables: Array<{
      label: string;
      tile_type: TablePlanTileType;
      position_x: number;
      position_y: number;
      rotation: 0 | 90 | 180 | 270;
      seat_count: number;
      is_top_table: boolean;
    }>,
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/apply-layout`, {
      template_key: templateKey,
      tables,
    });
  }

  quickSetup(
    eventId: number | string,
    payload: { top_table_size: number; target_body_size: 8 | 10 | 12 },
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/quick-setup`, payload);
  }

  /**
   * TVG-189: save the plan's current arrangement as a reusable per-venue
   * template. The geometry is snapshotted server-side from what is already on
   * the canvas, so only the name is sent.
   */
  saveTemplate(eventId: number | string, name: string): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/templates`, { name });
  }

  /** TVG-189: load a saved per-venue template onto the plan as a starting point. */
  applyTemplate(eventId: number | string, templateId: number): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/apply-template`, {
      template_id: templateId,
    });
  }

  /**
   * TVG-227: edit an existing saved per-venue template — rename it (`name`)
   * and/or overwrite its stored layout with the plan's current arrangement
   * (`sync_layout`). Either or both may be sent in one call.
   */
  updateTemplate(
    eventId: number | string,
    templateId: number,
    payload: { name?: string; sync_layout?: boolean },
  ): Observable<TablePlanResponse> {
    return this.http.patch<TablePlanResponse>(`${this.base(eventId)}/templates/${templateId}`, payload);
  }

  /** TVG-189: delete a saved per-venue template. */
  deleteTemplate(eventId: number | string, templateId: number): Observable<TablePlanResponse> {
    return this.http.delete<TablePlanResponse>(`${this.base(eventId)}/templates/${templateId}`);
  }

  /**
   * TVG-216: place a new fixed-area marker (bar, dance floor or toilet) on the
   * canvas. Visual only — no fit or capacity check.
   */
  addMarker(
    eventId: number | string,
    payload: {
      type: TablePlanMarkerType;
      label?: string;
      position_x: number;
      position_y: number;
      width: number;
      height: number;
    },
  ): Observable<TablePlanResponse> {
    return this.http.post<TablePlanResponse>(`${this.base(eventId)}/markers`, payload);
  }

  /** TVG-216: move, resize or relabel a marker (partial update). */
  updateMarker(
    eventId: number | string,
    markerId: number,
    payload: Partial<{
      label: string;
      position_x: number;
      position_y: number;
      width: number;
      height: number;
    }>,
  ): Observable<TablePlanResponse> {
    return this.http.put<TablePlanResponse>(`${this.base(eventId)}/markers/${markerId}`, payload);
  }

  /** TVG-216: remove a fixed-area marker from the canvas. */
  deleteMarker(eventId: number | string, markerId: number): Observable<TablePlanResponse> {
    return this.http.delete<TablePlanResponse>(`${this.base(eventId)}/markers/${markerId}`);
  }

  private base(eventId: number | string): string {
    return `${this.apiUrl}/api/events/${eventId}/table-plan`;
  }
}
